import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Bolt, CalendarMonth, DeleteOutline, EditOutlined, History, Pause, PowerSettingsNew, WarningAmber } from '@mui/icons-material';
import agentService from '../../services/agentService';
import scheduleService from '../../services/scheduleService';
import CompactExecutionLogs from '../shared/CompactExecutionLogs';
import ExecutionLogsDrawer from '../shared/ExecutionLogsDrawer';
import {
  buildAgentExecutionSections,
  filterLogsByDate,
  formatLogDateTime,
  getLogStatusMeta,
  getLogTimeValue,
  groupLogsByDate,
} from '../../utils/executionLogUtils';
import styles from './Schedules.module.scss';
import { CRON_FREQUENCY_OPTIONS, DEFAULT_CRON_BUILDER, buildCronFromBuilder, describeCron, formatDateTime, getUtcOffsetLabelForTimezone, inferBuilderFromCron } from '../../utils/cronUtils';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';

const TZ_FALLBACK = 'UTC';
const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const defaultForm = {
  agent_id: '',
  name: '',
  cron_expression: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || TZ_FALLBACK,
  default_query: '',
};

const TIMEZONE_OPTIONS = (() => {
  const fallbackOptions = [
    'UTC',
    'Asia/Kolkata',
    'America/New_York',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Berlin',
    'Asia/Dubai',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
  ];

  if (typeof Intl.supportedValuesOf === 'function') {
    return [...new Set([...fallbackOptions, ...Intl.supportedValuesOf('timeZone')])];
  }

  return fallbackOptions;
})();

const isScheduleLog = (log) => {
  const source = String(log.trigger_source || log.source || log.event_source || '').toLowerCase();
  return source === 'schedule' || source === 'scheduler';
};

const extractAgentVariables = (agent) => {
  if (!agent) return [];
  if (agent.variables) {
    return String(agent.variables).split(',').map((variable) => variable.trim()).filter(Boolean);
  }

  const matches = String(agent.agent_instructions || '').match(/\{([^{}]+)\}/g) || [];
  return [...new Set(matches.map((match) => match.slice(1, -1).trim()).filter(Boolean))];
};

const parseVariablesJson = (value) => {
  if (!value?.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const formatTimezoneOption = (timezone) => `${timezone} (${getUtcOffsetLabelForTimezone(timezone)})`;

const parseCronField = (field, min, max) => {
  const normalized = String(field || '*').trim();
  if (normalized === '*' || normalized === '?') {
    return new Set(Array.from({ length: max - min + 1 }, (_, index) => min + index));
  }

  const values = new Set();
  normalized.split(',').forEach((part) => {
    const [rangePart, stepPart] = part.split('/');
    const step = Math.max(1, Number(stepPart) || 1);
    let start = min;
    let end = max;

    if (rangePart !== '*') {
      if (rangePart.includes('-')) {
        const [rangeStart, rangeEnd] = rangePart.split('-').map(Number);
        start = Number.isFinite(rangeStart) ? rangeStart : min;
        end = Number.isFinite(rangeEnd) ? rangeEnd : max;
      } else {
        const exactValue = Number(rangePart);
        if (Number.isFinite(exactValue)) {
          start = exactValue;
          end = exactValue;
        }
      }
    }

    for (let value = start; value <= end; value += step) {
      if (value >= min && value <= max) values.add(value);
    }
  });

  return values;
};

const getZonedDateParts = (date, timezone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || undefined,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).formatToParts(date);
    const map = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, Number(part.value)]));
    const dayOfWeek = new Date(Date.UTC(map.year, map.month - 1, map.day)).getUTCDay();

    return {
      second: map.second,
      minute: map.minute,
      hour: map.hour,
      dayOfMonth: map.day,
      month: map.month,
      dayOfWeek,
    };
  } catch {
    return {
      second: date.getSeconds(),
      minute: date.getMinutes(),
      hour: date.getHours(),
      dayOfMonth: date.getDate(),
      month: date.getMonth() + 1,
      dayOfWeek: date.getDay(),
    };
  }
};

const estimateUpcomingRuns = (cronExpression, timezone, count = 5) => {
  const fields = String(cronExpression || '').trim().split(/\s+/).filter(Boolean);
  if (![5, 6].includes(fields.length)) return [];

  const [secondField, minuteField, hourField, dayOfMonthField, monthField, dayOfWeekField] =
    fields.length === 5 ? ['0', ...fields] : fields;

  const allowed = {
    seconds: parseCronField(secondField, 0, 59),
    minutes: parseCronField(minuteField, 0, 59),
    hours: parseCronField(hourField, 0, 23),
    daysOfMonth: parseCronField(dayOfMonthField, 1, 31),
    months: parseCronField(monthField, 1, 12),
    daysOfWeek: parseCronField(dayOfWeekField, 0, 7),
  };

  const results = [];
  const stepMs = secondField.includes('*') || secondField.includes('/') ? 1000 : 60000;
  let candidate = new Date(Date.now() + stepMs);
  if (stepMs === 60000) {
    candidate.setSeconds(0, 0);
  }

  let attempts = 0;
  const maxAttempts = stepMs === 1000 ? 500000 : 250000;

  while (results.length < count && attempts < maxAttempts) {
    const parts = getZonedDateParts(candidate, timezone);
    const matchesDayOfWeek = allowed.daysOfWeek.has(parts.dayOfWeek) || (parts.dayOfWeek === 0 && allowed.daysOfWeek.has(7));

    if (
      allowed.seconds.has(parts.second) &&
      allowed.minutes.has(parts.minute) &&
      allowed.hours.has(parts.hour) &&
      allowed.daysOfMonth.has(parts.dayOfMonth) &&
      allowed.months.has(parts.month) &&
      matchesDayOfWeek
    ) {
      results.push(new Date(candidate));
    }

    candidate = new Date(candidate.getTime() + stepMs);
    attempts += 1;
  }

  return results;
};

const Schedules = () => {
  const { has } = usePermissions();
  // Creating/editing a schedule needs the `schedules` permission (spec §2.2).
  // Listing, toggling, running, viewing runs and deleting stay available to all.
  const canManageSchedules = has(PERMISSIONS.SCHEDULES);
  const [searchParams] = useSearchParams();
  const queryAgentId = searchParams.get('agentId');
  const [agents, setAgents] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(defaultForm);
  const [builder, setBuilder] = useState(DEFAULT_CRON_BUILDER);
  const [variableValues, setVariableValues] = useState({});
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logDateFilter, setLogDateFilter] = useState('all');
  const [customLogDate, setCustomLogDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(false);

  const loadAgentsAll = useCallback(async () => {
    const limit = 100;
    let page = 1;
    let totalPages = 1;
    const allAgents = [];

    while (page <= totalPages) {
      const response = await agentService.getAgents(page, limit);
      const pageAgents = response?.agents || [];
      allAgents.push(...pageAgents);
      totalPages = response?.total_pages || 1;
      page += 1;
    }

    return allAgents;
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [schedulesResponse, agentList] = await Promise.all([
        scheduleService.listSchedules(),
        loadAgentsAll().catch(() => []),
      ]);
      setSchedules(Array.isArray(schedulesResponse) ? schedulesResponse : []);
      if (agentList?.length) setAgents(agentList);
    } catch (e) {
      console.error('Failed loading schedules:', e);
      setError('Failed to load schedules. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [loadAgentsAll]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const agentNameMap = useMemo(() => {
    const map = new Map();
    agents.forEach((agent) => map.set(String(agent.id), agent.name || `Agent ${agent.id}`));
    return map;
  }, [agents]);

  const getScheduleAgentName = useCallback((schedule) => {
    return (
      schedule.agent_name
      || schedule.agent?.name
      || agentNameMap.get(String(schedule.agent_id))
      || (schedule.agent_id ? `Agent ${schedule.agent_id}` : 'Unknown agent')
    );
  }, [agentNameMap]);

  const filteredSchedules = useMemo(() => {
    return schedules.filter((item) => {
      const matchesAgent = !queryAgentId || String(item.agent_id) === String(queryAgentId);
      const status = String(item.status || '').toLowerCase();
      const isActive = status === 'active';
      const isInactive = ['inactive', 'paused', 'disabled', 'deactivated'].includes(status) || !status;
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && isActive) ||
        (statusFilter === 'inactive' && isInactive);

      return matchesAgent && matchesStatus;
    });
  }, [schedules, queryAgentId, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: schedules.length, active: 0, inactive: 0 };
    schedules.forEach((item) => {
      const status = String(item.status || '').toLowerCase();
      if (status === 'active') counts.active += 1;
      else counts.inactive += 1;
    });
    return counts;
  }, [schedules]);

  const selectedAgent = useMemo(() => {
    return agents.find((agent) => String(agent.id) === String(form.agent_id));
  }, [agents, form.agent_id]);

  const detectedVariables = useMemo(() => extractAgentVariables(selectedAgent), [selectedAgent]);

  useEffect(() => {
    if (builder.frequency === 'custom') return;
    const nextCron = buildCronFromBuilder(builder, '');
    setForm((prev) => (prev.cron_expression === nextCron ? prev : { ...prev, cron_expression: nextCron }));
  }, [builder]);

  const timezoneOptions = useMemo(() => {
    return [...new Set([form.timezone || TZ_FALLBACK, ...TIMEZONE_OPTIONS])];
  }, [form.timezone]);

  const upcomingRuns = useMemo(() => {
    if (!describeCron(form.cron_expression)) return [];
    return estimateUpcomingRuns(form.cron_expression, form.timezone, 5);
  }, [form.cron_expression, form.timezone]);

  const scheduleLogs = useMemo(() => logs.filter(isScheduleLog), [logs]);

  const groupedLogs = useMemo(() => {
    const filtered = filterLogsByDate(scheduleLogs, logDateFilter, customLogDate);
    return groupLogsByDate(filtered);
  }, [scheduleLogs, logDateFilter, customLogDate]);

  const ensureAgentsLoaded = useCallback(async () => {
    if (agents.length > 0) return;
    const data = await loadAgentsAll();
    setAgents(data || []);
  }, [agents.length, loadAgentsAll]);

  const openCreate = async () => {
    await ensureAgentsLoaded();
    setEditingSchedule(null);
    setForm({
      ...defaultForm,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || TZ_FALLBACK,
      agent_id: queryAgentId || '',
      cron_expression: buildCronFromBuilder(DEFAULT_CRON_BUILDER, ''),
    });
    setBuilder(DEFAULT_CRON_BUILDER);
    setVariableValues({});
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = async (schedule) => {
    await ensureAgentsLoaded();
    setEditingSchedule(schedule);
    const cron = schedule.cron_expression || '';
    setForm({
      agent_id: String(schedule.agent_id || ''),
      name: schedule.name || '',
      cron_expression: cron,
      timezone: schedule.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || TZ_FALLBACK,
      default_query: schedule.default_query || '',
    });
    setBuilder(inferBuilderFromCron(cron));
    const savedVars = schedule.default_variables;
    if (savedVars && typeof savedVars === 'object' && !Array.isArray(savedVars)) {
      setVariableValues(savedVars);
    } else if (typeof savedVars === 'string' && savedVars.trim()) {
      setVariableValues(parseVariablesJson(savedVars));
    } else {
      setVariableValues({});
    }
    setFormError('');
    setDialogOpen(true);
  };

  const buildDefaultVariables = () => {
    const result = {};
    Object.entries(variableValues).forEach(([key, value]) => {
      if (String(value ?? '').trim()) result[key] = value;
    });
    return Object.keys(result).length ? result : null;
  };

  const saveSchedule = async () => {
    try {
      setFormError('');
      if (!form.agent_id) throw new Error('Please choose an agent.');
      if (!form.name.trim()) throw new Error('Please enter a schedule name.');
      if (!form.cron_expression.trim()) throw new Error('Please enter a cron expression.');
      if (!form.default_query.trim()) throw new Error('Please add a default query.');
      if (!describeCron(form.cron_expression.trim())) throw new Error('Cron expression is invalid.');

      const payload = {
        agent_id: Number(form.agent_id),
        name: form.name.trim(),
        cron_expression: form.cron_expression.trim(),
        timezone: form.timezone || TZ_FALLBACK,
        default_query: form.default_query.trim(),
        default_variables: buildDefaultVariables(),
      };

      setSubmitting(true);
      if (editingSchedule?.id) await scheduleService.updateSchedule(editingSchedule.id, payload);
      else await scheduleService.createSchedule(payload);

      await loadData();
      setDialogOpen(false);
    } catch (e) {
      setFormError(e.message || 'Failed to save schedule.');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (id, action) => {
    try {
      if (action === 'toggle') await scheduleService.toggleSchedule(id);
      if (action === 'run') await scheduleService.runScheduleNow(id);
      await loadData();
    } catch (e) {
      console.error('Schedule action failed:', e);
    }
  };

  const openDeleteDialog = (schedule) => {
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingSchedule) return;
    setDeleteDialogOpen(false);
    setScheduleToDelete(null);
  };

  const confirmDeleteSchedule = async () => {
    if (!scheduleToDelete?.id) return;
    try {
      setDeletingSchedule(true);
      await scheduleService.deleteSchedule(scheduleToDelete.id);
      await loadData();
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
    } catch (e) {
      console.error('Failed to delete schedule:', e);
    } finally {
      setDeletingSchedule(false);
    }
  };

  const loadLogs = useCallback(async (schedule = selectedSchedule) => {
    if (!schedule?.id) return;
    setLoadingLogs(true);
    try {
      const response = await scheduleService.listScheduleLogs(schedule.id, 50);
      setLogs(Array.isArray(response) ? response.filter(isScheduleLog) : []);
    } catch (e) {
      console.error('Failed to fetch logs:', e);
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, [selectedSchedule]);

  const openLogs = async (schedule) => {
    setSelectedSchedule(schedule);
    setLogs([]);
    setLogDateFilter('all');
    setCustomLogDate('');
    setLogsDrawerOpen(true);
    await loadLogs(schedule);
  };

  const clearLogs = async () => {
    if (!selectedSchedule?.id) return;
    try {
      await scheduleService.clearScheduleLogs(selectedSchedule.id);
      setLogs([]);
    } catch (e) {
      console.error('Failed to clear schedule logs:', e);
    }
  };

  const updateVariableValue = (variable, value) => {
    setVariableValues((prev) => ({
      ...prev,
      [variable]: value,
    }));
  };

  return (
    <Box className={styles.schedulesLayout}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h4" component="h1">Schedules</Typography>
          <Typography variant="body2">Manage cron-based agent automation and inspect execution runs.</Typography>
        </Box>
        <Box className={styles.headerActions}>
          {canManageSchedules && (
            <Button variant="contained" className={styles.addButton} startIcon={<Add />} onClick={openCreate}>New Schedule</Button>
          )}
        </Box>
      </Box>

      <Box className={styles.filterBar}>
        <Typography className={styles.filterLabel}>Filter schedules</Typography>
        <Box className={styles.filterPills}>
          {STATUS_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              size="small"
              disableRipple
              disableTouchRipple
              className={`${styles.filterPill} ${statusFilter === filter.value ? styles.filterPillActive : ''}`}
              onClick={() => setStatusFilter(filter.value)}
            >
              {filter.label}
              <span>{statusCounts[filter.value] || 0}</span>
            </Button>
          ))}
        </Box>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box className={styles.loadingWrap}><CircularProgress /></Box>
      ) : filteredSchedules.length === 0 ? (
        <Box className={styles.emptyState}>
          <CalendarMonth className={styles.emptyIcon} />
          <Typography variant="h6">No schedules found</Typography>
          <Typography variant="body2">Create your first schedule to automate agent runs.</Typography>
        </Box>
      ) : (
        <Box className={styles.scheduleList}>
          {filteredSchedules.map((schedule) => {
            const isActive = String(schedule.status).toLowerCase() === 'active';
            return (
              <Box key={schedule.id} className={styles.scheduleRow}>
                <Box className={styles.scheduleMain}>
                  <Box className={styles.rowTitleLine}>
                    <Typography className={styles.scheduleName}>{schedule.name}</Typography>
                    <Chip size="small" label={isActive ? 'Active' : schedule.status || 'Paused'} className={isActive ? styles.activeChip : styles.inactiveChip} />
                  </Box>
                    <Typography className={styles.agentName}>
                      Agent: {getScheduleAgentName(schedule)}
                    </Typography>
                  <Box className={styles.inlineDetails}>
                    <Typography className={styles.cronExpression}>{schedule.cron_expression}</Typography>
                    <Typography className={styles.cronHint}>{describeCron(schedule.cron_expression) || 'Invalid cron expression'}</Typography>
                  </Box>
                  <Typography className={styles.queryPreview}>Query: {schedule.default_query}</Typography>
                </Box>

                <Box className={styles.scheduleMeta}>
                  <Box className={styles.metaBlock}>
                    <Typography className={styles.sectionLabel}>Next Run</Typography>
                    <Typography className={styles.metaValue}>{formatDateTime(schedule.next_run_at)}</Typography>
                  </Box>
                  <Box className={styles.metaBlock}>
                    <Typography className={styles.sectionLabel}>Last Run</Typography>
                    <Typography className={styles.metaValue}>{formatDateTime(schedule.last_run_at)}</Typography>
                  </Box>
                  <Typography className={styles.timezoneText}>{formatTimezoneOption(schedule.timezone || TZ_FALLBACK)}</Typography>
                </Box>

                <Box className={styles.rowActions}>
                  <Tooltip title={isActive ? 'Pause schedule' : 'Activate schedule'}>
                    <IconButton onClick={() => updateStatus(schedule.id, 'toggle')} size="small" className={styles.iconButton}>
                      {isActive ? <Pause fontSize="small" /> : <PowerSettingsNew fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Run now"><IconButton onClick={() => updateStatus(schedule.id, 'run')} size="small" className={styles.iconButton}><Bolt fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="View runs"><IconButton onClick={() => openLogs(schedule)} size="small" className={styles.iconButton}><History fontSize="small" /></IconButton></Tooltip>
                  {canManageSchedules && (
                    <Tooltip title="Edit schedule"><IconButton onClick={() => openEdit(schedule)} size="small" className={styles.iconButton}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                  )}
                  <Tooltip title="Delete schedule"><IconButton onClick={() => openDeleteDialog(schedule)} size="small" className={`${styles.iconButton} ${styles.deleteButton}`}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} fullWidth maxWidth="md" classes={{ paper: styles.dialogPaper }}>
        <DialogTitle className={styles.dialogTitle}>{editingSchedule ? 'Edit Schedule' : 'Create Schedule'}</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <Box className={styles.formGrid}>
            <TextField
              select
              label="Agent"
              value={form.agent_id}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, agent_id: e.target.value }));
                setVariableValues({});
              }}
              required
              size="small"
              className={styles.formField}
              InputLabelProps={{ shrink: true }}
            >
              {agents.map((agent) => <MenuItem key={agent.id} value={String(agent.id)}>{agent.name || `Agent ${agent.id}`}</MenuItem>)}
            </TextField>
            <TextField
              label="Schedule Name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              required
              size="small"
              className={styles.formField}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Timezone"
              value={form.timezone}
              onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
              size="small"
              className={styles.formField}
              InputLabelProps={{ shrink: true }}
            >
              {timezoneOptions.map((timezone) => (
                <MenuItem key={timezone} value={timezone}>{formatTimezoneOption(timezone)}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Box className={styles.builderWrap}>
            <Typography variant="subtitle2" className={styles.builderTitle}>Simple Scheduler Builder</Typography>
            <Box className={styles.builderRow}>
              <TextField
                select
                size="small"
                label="Frequency"
                value={builder.frequency}
                className={styles.formField}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setBuilder((prev) => ({ ...prev, frequency: e.target.value }))}
              >
                {CRON_FREQUENCY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </TextField>
              {(builder.frequency === 'hourly' ||
                builder.frequency === 'daily' ||
                builder.frequency === 'weekly' ||
                builder.frequency === 'monthly') && (
                <TextField
                  size="small"
                  type="number"
                  label="Minute"
                  value={builder.minute}
                  className={styles.formField}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setBuilder((prev) => ({ ...prev, minute: e.target.value }))}
                  inputProps={{ min: 0, max: 59 }}
                />
              )}
              {(builder.frequency === 'daily' ||
                builder.frequency === 'weekly' ||
                builder.frequency === 'monthly') && (
                <TextField
                  size="small"
                  type="number"
                  label="Hour"
                  value={builder.hour}
                  className={styles.formField}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setBuilder((prev) => ({ ...prev, hour: e.target.value }))}
                  inputProps={{ min: 0, max: 23 }}
                />
              )}
              {builder.frequency === 'weekly' && (
                <TextField
                  select
                  size="small"
                  label="Day of week"
                  value={builder.dayOfWeek}
                  className={styles.formField}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setBuilder((prev) => ({ ...prev, dayOfWeek: e.target.value }))}
                >
                  <MenuItem value={0}>Sunday</MenuItem>
                  <MenuItem value={1}>Monday</MenuItem>
                  <MenuItem value={2}>Tuesday</MenuItem>
                  <MenuItem value={3}>Wednesday</MenuItem>
                  <MenuItem value={4}>Thursday</MenuItem>
                  <MenuItem value={5}>Friday</MenuItem>
                  <MenuItem value={6}>Saturday</MenuItem>
                </TextField>
              )}
              {builder.frequency === 'monthly' && (
                <TextField
                  size="small"
                  type="number"
                  label="Day of month"
                  value={builder.dayOfMonth}
                  className={styles.formField}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setBuilder((prev) => ({ ...prev, dayOfMonth: e.target.value }))}
                  inputProps={{ min: 1, max: 31 }}
                />
              )}
            </Box>
          </Box>

          <TextField
            label="Cron Expression"
            value={form.cron_expression}
            onChange={(e) => {
              const value = e.target.value;
              setForm((prev) => ({ ...prev, cron_expression: value }));
              setBuilder(inferBuilderFromCron(value));
            }}
            required
            fullWidth
            className={styles.formField}
            InputLabelProps={{ shrink: true }}
          />
          <Typography className={styles.cronDescription}>{describeCron(form.cron_expression) || 'Enter a valid cron expression to preview what it means.'}</Typography>
          <Box className={styles.executionSummary}>
            <Box>
              <Typography className={styles.executionSummaryTitle}>Estimated Upcoming Executions</Typography>
              <Typography className={styles.executionSummaryHint}>Preview based on the selected timezone. Times are shown in your local time.</Typography>
            </Box>
            {upcomingRuns.length > 0 ? (
              <Box className={styles.executionList}>
                {upcomingRuns.map((runAt) => (
                  <Box key={runAt.toISOString()} className={styles.executionItem}>
                    <span>{formatDateTime(runAt)}</span>
                    <small>{formatDateTime(runAt, form.timezone)} scheduler time</small>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography className={styles.executionEmpty}>Add a valid cron expression to preview upcoming runs.</Typography>
            )}
          </Box>
          {detectedVariables.length > 0 && (
            <Box className={styles.variablesPanel}>
              <Box>
                <Typography className={styles.variablesTitle}>Detected Agent Variables</Typography>
                <Typography className={styles.variablesHint}>Add default values for scheduled runs. These are sent as JSON variables with each execution.</Typography>
              </Box>
              <Box className={styles.variableChips}>
                {detectedVariables.map((variable) => (
                  <Chip key={variable} size="small" label={`{${variable}}`} className={styles.variableChip} />
                ))}
              </Box>
              <Box className={styles.variableFields}>
                {detectedVariables.map((variable) => (
                  <TextField
                    key={variable}
                    label={variable}
                    value={variableValues[variable] || ''}
                    onChange={(event) => updateVariableValue(variable, event.target.value)}
                    size="small"
                    className={styles.formField}
                    InputLabelProps={{ shrink: true }}
                  />
                ))}
              </Box>
            </Box>
          )}
          <TextField
            label="Default Query"
            value={form.default_query}
            onChange={(e) => setForm((prev) => ({ ...prev, default_query: e.target.value }))}
            required
            fullWidth
            multiline
            rows={3}
            className={styles.formField}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveSchedule} disabled={submitting} className={styles.addButton} variant="contained">
            {submitting ? <CircularProgress size={18} color="inherit" /> : editingSchedule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ExecutionLogsDrawer
        open={logsDrawerOpen}
        onClose={() => setLogsDrawerOpen(false)}
        title="Schedule Runs"
        subtitle={selectedSchedule?.name || 'Selected schedule'}
        loading={loadingLogs}
        onRefresh={() => loadLogs()}
        onClear={clearLogs}
        refreshDisabled={!selectedSchedule}
        clearDisabled={!selectedSchedule || logs.length === 0}
      >
        <CompactExecutionLogs
          groupedLogs={groupedLogs}
          loading={loadingLogs}
          emptyMessage="No runs found."
          filteredEmptyMessage="No runs found for this date range."
          showDateFilters
          logDateFilter={logDateFilter}
          onLogDateFilterChange={setLogDateFilter}
          customLogDate={customLogDate}
          onCustomLogDateChange={setCustomLogDate}
          resetKey={selectedSchedule?.id}
          resolveLogId={(log, index) => log.id || `${getLogTimeValue(log) || 'run'}-${index}`}
          resolveHeader={(log) => ({
            statusMeta: getLogStatusMeta(log.status),
            source: log.trigger_source || 'schedule',
            time: formatLogDateTime(getLogTimeValue(log)),
            error: log.error_message,
          })}
          resolveSections={buildAgentExecutionSections}
        />
      </ExecutionLogsDrawer>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} fullWidth maxWidth="sm" classes={{ paper: styles.dialogPaper }}>
        <DialogTitle className={styles.dialogTitle}>Delete Schedule</DialogTitle>
        <DialogContent className={styles.deleteDialogContent}>
          <Box className={styles.warningIconWrap}>
            <WarningAmber />
          </Box>
          <Typography className={styles.deleteDialogHeading}>Delete this schedule?</Typography>
          <Typography className={styles.deleteDialogText}>
            This will permanently delete "{scheduleToDelete?.name || 'this schedule'}" and stop future automated runs. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeDeleteDialog} disabled={deletingSchedule} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteSchedule}
            disabled={deletingSchedule}
            variant="contained"
            className={styles.dangerButton}
            startIcon={deletingSchedule ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
          >
            {deletingSchedule ? 'Deleting...' : 'Delete Schedule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Schedules;
